import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Wallet, Landmark, CreditCard, CheckCircle2, Clock, XCircle, Eye, Pencil, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import { DatePicker } from "@/components/DatePicker";

const METHOD_META = {
  cash: { label: "Contanti", Icon: Wallet, color: "amber" },
  bank: { label: "Bonifico", Icon: Landmark, color: "blue" },
  stripe: { label: "Carta", Icon: CreditCard, color: "indigo" },
  paypal: { label: "PayPal", Icon: CreditCard, color: "sky" },
};

const STATUS_META = {
  paid: { label: "Pagato", color: "emerald", Icon: CheckCircle2 },
  pending: { label: "In attesa", color: "amber", Icon: Clock },
  expired: { label: "Scaduto", color: "slate", Icon: XCircle },
  cancelled: { label: "Annullato", color: "red", Icon: XCircle },
};

export default function AdminPayments() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [editTx, setEditTx] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/payments");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const confirmTx = async (tx) => {
    if (!window.confirm(`Confermare l'incasso di ${formatCurrency(tx.amount)} da ${tx.client_name}?`)) return;
    try {
      await api.post(`/payments/${tx.id}/confirm`);
      toast.success("Pagamento confermato e cliente notificato");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openEdit = (tx) => {
    setEditTx(tx);
    setEditForm({
      amount: tx.amount,
      method: tx.method,
      payment_status: tx.payment_status,
      paid_at: tx.paid_at ? tx.paid_at.slice(0, 10) : "",
      reference: tx.reference || "",
      notes: tx.admin_notes || "",
    });
  };

  const saveEdit = async () => {
    if (!editTx) return;
    setSaving(true);
    try {
      const payload = { ...editForm };
      payload.amount = parseFloat(payload.amount);
      if (Number.isNaN(payload.amount)) { toast.error("Importo non valido"); setSaving(false); return; }
      if (payload.paid_at) payload.paid_at = new Date(payload.paid_at + "T12:00:00").toISOString();
      else delete payload.paid_at;
      await api.put(`/payments/${editTx.id}`, payload);
      toast.success("Pagamento aggiornato");
      setEditTx(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const deleteTx = async (tx) => {
    // Doppia conferma per cancellazione irreversibile
    if (!window.confirm(
      `⚠️ ATTENZIONE: stai per ELIMINARE il pagamento di ${formatCurrency(tx.amount)} da ${tx.client_name}.\n\n` +
      `Metodo: ${tx.method.toUpperCase()} · Stato: ${tx.payment_status}\n` +
      `Riferimento: ${tx.reference}\n\n` +
      "L'operazione è IRREVERSIBILE e aggiornerà l'ultima data pagamento della polizza.\n\nVuoi continuare?"
    )) return;
    if (!window.confirm(
      `⚠️ ULTIMA CONFERMA: confermi l'eliminazione definitiva di questa transazione?\n\n` +
      "Premi OK per cancellare. Premi Annulla per mantenere lo storico."
    )) return;
    try {
      await api.delete(`/payments/${tx.id}`);
      toast.success("Pagamento eliminato");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = items.filter((t) => {
    // Filtro stato
    if (filter === "pending" && !(t.payment_status === "pending" && (t.method === "cash" || t.method === "bank"))) return false;
    else if (filter !== "all" && filter !== "pending" && t.payment_status !== filter) return false;
    // 🆕 28/07/2026 — Filtro data (range Da → A) su created_at
    if (dateFrom || dateTo) {
      const created = String(t.created_at || "").slice(0, 10);
      if (!created) return false;
      if (dateFrom && created < dateFrom) return false;
      if (dateTo && created > dateTo) return false;
    }
    // Filtro ricerca full-text (cliente, polizza/offerta, metodo, importo, riferimento)
    const q = (search || "").trim().toLowerCase();
    if (!q) return true;
    const method = (METHOD_META[t.method]?.label || t.method || "").toLowerCase();
    const status = (STATUS_META[t.payment_status]?.label || t.payment_status || "").toLowerCase();
    const hay = [
      t.client_name,
      t.metadata?.policy_number,
      t.offer_number,
      t.reference,
      method,
      status,
      String(t.amount ?? ""),
      t.method,
      t.payment_status,
    ].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q);
  });

  return (
    <div className="space-y-6 animate-fade-up" data-testid="admin-payments-page">
      <header>
        <p className="text-xs uppercase tracking-[0.2em] text-blue-600 mb-2">Pagamenti polizze</p>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight flex items-center gap-3"><Wallet className="h-8 w-8 sm:h-10 sm:w-10 text-emerald-600 bg-emerald-100 rounded-lg p-1.5" />Storico pagamenti</h1>
        <p className="text-slate-500 mt-1">{items.length} transazioni totali · {items.filter((t) => t.payment_status === "pending" && (t.method === "cash" || t.method === "bank")).length} in attesa di conferma manuale</p>
      </header>

      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs uppercase tracking-wide text-slate-500 mr-1">Filtro:</span>
        {[
          { v: "pending", l: "Da confermare" },
          { v: "paid", l: "Pagati" },
          { v: "all", l: "Tutti" },
        ].map((opt) => (
          <button
            key={opt.v}
            data-testid={`pay-filter-${opt.v}`}
            onClick={() => setFilter(opt.v)}
            className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
              filter === opt.v ? "bg-blue-600 text-white border-blue-600" : "border-slate-200 text-slate-600 hover:border-slate-300"
            }`}
          >{opt.l}</button>
        ))}
        {/* 🆕 28/07/2026 — Filtro range data (created_at) */}
        <div className="flex items-center gap-1.5 ml-2">
          <span className="text-[11px] uppercase tracking-wide text-slate-500">Dal</span>
          <DatePicker
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value || "")}
            placeholder="—"
            className="h-8 text-xs w-[130px]"
            data-testid="payments-date-from"
          />
          <span className="text-[11px] uppercase tracking-wide text-slate-500">al</span>
          <DatePicker
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value || "")}
            placeholder="—"
            className="h-8 text-xs w-[130px]"
            data-testid="payments-date-to"
          />
          {(dateFrom || dateTo) && (
            <button
              type="button"
              onClick={() => { setDateFrom(""); setDateTo(""); }}
              className="text-[11px] text-slate-500 hover:text-rose-600 underline"
              data-testid="payments-date-clear"
              title="Rimuovi filtro data"
            >reset</button>
          )}
        </div>
        {/* 🆕 28/07/2026 — Ricerca full-text (cliente, polizza/offerta, riferimento, metodo, importo) */}
        <div className="ml-auto flex items-center gap-1.5 relative">
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="🔍 Cerca cliente, polizza, offerta, riferimento…"
            className="h-8 text-xs min-w-[260px] sm:min-w-[340px] pr-8"
            data-testid="payments-search-input"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 text-xs"
              title="Pulisci ricerca"
              data-testid="payments-search-clear"
            >✕</button>
          )}
          <span className="text-[11px] text-slate-500 whitespace-nowrap" data-testid="payments-search-count">
            {filtered.length}/{items.length}
          </span>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-emerald-600 text-white text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-6 py-3">Data</th>
              <th className="text-left px-6 py-3">Cliente</th>
              <th className="text-left px-6 py-3">Polizza</th>
              <th className="text-left px-6 py-3">Metodo</th>
              <th className="text-right px-6 py-3">Importo</th>
              <th className="text-center px-6 py-3">Stato</th>
              <th className="text-right px-6 py-3">Azioni</th>
            </tr>
          </thead>
          <tbody data-testid="payments-table">
            {filtered.length === 0 && (
              <tr><td colSpan="7" className="px-6 py-10 text-center text-slate-400 text-xs">Nessuna transazione per il filtro selezionato.</td></tr>
            )}
            {filtered.map((tx) => {
              const m = METHOD_META[tx.method] || METHOD_META.cash;
              const MIcon = m.Icon;
              const s = STATUS_META[tx.payment_status] || STATUS_META.pending;
              const SIcon = s.Icon;
              return (
                <tr key={tx.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`payment-row-${tx.id}`}>
                  <td className="px-6 py-4 text-xs text-slate-500 whitespace-nowrap">
                    {formatDate(tx.created_at)}<br />
                    <span className="text-[10px]">{new Date(tx.created_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</span>
                  </td>
                  <td className="px-6 py-4 font-medium">{tx.client_name}</td>
                  <td className="px-6 py-4 text-xs">
                    <div className="font-mono">{tx.metadata?.policy_number || tx.offer_number || "—"}</div>
                    <div className="text-slate-400 text-[10px] font-mono">{tx.reference}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded bg-${m.color}-50 text-${m.color}-700 border border-${m.color}-200`}>
                      <MIcon className="h-3 w-3" /> {m.label}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right tabular-nums font-semibold">{formatCurrency(tx.amount)}</td>
                  <td className="px-6 py-4 text-center">
                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-${s.color}-50 text-${s.color}-700 border border-${s.color}-200`}>
                      <SIcon className="h-3 w-3" /> {s.label}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right space-x-1 whitespace-nowrap">
                    {tx.payment_status === "pending" && (tx.method === "cash" || tx.method === "bank") && (
                      <Button
                        data-testid={`confirm-payment-${tx.id}`}
                        size="sm"
                        onClick={() => confirmTx(tx)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8"
                      >
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Conferma
                      </Button>
                    )}
                    {/* 🆕 28/07/2026 — Il pulsante Ricevuta è visibile solo se la tx ha diritto a una quietanza
                        (has_receipt esplicito o receipt_id valorizzato). Le tx da quote_offer non generano ricevuta. */}
                    {(tx.has_receipt === true || tx.receipt_id) ? (
                      <button
                        data-testid={`view-receipt-${tx.id}`}
                        onClick={() => navigate(`/admin/pagamenti/ricevuta/${tx.id}`)}
                        className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1 text-xs px-2 py-1"
                        title="Vedi ricevuta"
                      >
                        <Eye className="h-3.5 w-3.5" /> Ricevuta
                      </button>
                    ) : (
                      <button
                        data-testid={`view-receipt-disabled-${tx.id}`}
                        disabled
                        className="text-slate-400 inline-flex items-center gap-1 text-xs px-2 py-1 cursor-not-allowed opacity-60"
                        title="Nessuna quietanza disponibile per questo pagamento"
                      >
                        <Eye className="h-3.5 w-3.5" /> Ricevuta
                      </button>
                    )}
                    <button
                      data-testid={`edit-payment-${tx.id}`}
                      onClick={() => openEdit(tx)}
                      className="text-amber-600 hover:text-amber-800 inline-flex items-center gap-1 text-xs px-2 py-1"
                      title="Modifica pagamento"
                    >
                      <Pencil className="h-3.5 w-3.5" /> Modifica
                    </button>
                    <button
                      data-testid={`delete-payment-${tx.id}`}
                      onClick={() => deleteTx(tx)}
                      className="text-red-600 hover:text-red-800 inline-flex items-center gap-1 text-xs px-2 py-1"
                      title="Elimina pagamento"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Elimina
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Dialog Modifica pagamento */}
      <Dialog open={!!editTx} onOpenChange={(o) => !o && setEditTx(null)}>
        <DialogContent className="max-w-lg" data-testid="edit-payment-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Pencil className="h-5 w-5" /> Modifica pagamento</DialogTitle>
          </DialogHeader>
          {editTx && (
            <div className="space-y-3">
              <div className="text-xs bg-slate-50 border border-slate-200 rounded p-2">
                <div><span className="text-slate-500">Cliente:</span> <strong>{editTx.client_name}</strong></div>
                <div><span className="text-slate-500">Polizza:</span> {editTx.metadata?.policy_number}</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Importo (€)</Label>
                  <Input
                    data-testid="edit-amount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={editForm.amount}
                    onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Metodo</Label>
                  <Select value={editForm.method} onValueChange={(v) => setEditForm({ ...editForm, method: v })}>
                    <SelectTrigger data-testid="edit-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Contanti</SelectItem>
                      <SelectItem value="bank">Bonifico</SelectItem>
                      <SelectItem value="stripe">Carta (Stripe)</SelectItem>
                      <SelectItem value="paypal">PayPal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Stato</Label>
                  <Select value={editForm.payment_status} onValueChange={(v) => setEditForm({ ...editForm, payment_status: v })}>
                    <SelectTrigger data-testid="edit-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">In attesa</SelectItem>
                      <SelectItem value="paid">Pagato</SelectItem>
                      <SelectItem value="cancelled">Annullato</SelectItem>
                      <SelectItem value="expired">Scaduto</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Data pagamento</Label>
                  <DatePicker data-testid="edit-paid-at" value={editForm.paid_at || ""}
                    onChange={(e) => setEditForm({ ...editForm, paid_at: e.target.value })} />
                </div>
                <div className="col-span-2">
                  <Label>Causale</Label>
                  <Input
                    data-testid="edit-reference"
                    value={editForm.reference || ""}
                    onChange={(e) => setEditForm({ ...editForm, reference: e.target.value })}
                  />
                </div>
                <div className="col-span-2">
                  <Label>Note interne (admin)</Label>
                  <Textarea
                    data-testid="edit-notes"
                    rows={2}
                    value={editForm.notes || ""}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                    placeholder="Es. Correzione importo concordata con cliente il…"
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTx(null)}>Annulla</Button>
            <Button data-testid="save-edit-payment" onClick={saveEdit} disabled={saving} className="bg-[#0F172A] hover:bg-[#1E293B]">
              <Save className="h-4 w-4 mr-2" /> {saving ? "Salvataggio…" : "Salva modifiche"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
